import React, { useState, useEffect } from 'react';
import { useMutation } from '@tanstack/react-query';
import PropTypes from 'prop-types';
import { Button, Input, TextArea, toast } from '../../components/common';
import { catalogApi } from '../../services/api';

export default function UOMForm({ uom, onSuccess, onCancel }) {
    const isEditing = !!uom;

    const [form, setForm] = useState({
        code: '',
        name: '',
        description: ''
    });
    const [errors, setErrors] = useState({});

    useEffect(() => {
        if (uom) {
            setForm({
                code: uom.code || '',
                name: uom.name || '',
                description: uom.description || ''
            });
        }
    }, [uom]);

    const mutation = useMutation({
        mutationFn: (data) => isEditing
            ? (catalogApi.updateUOM?.(uom.id, data) || Promise.resolve())
            : catalogApi.createUOM(data),
        onSuccess: () => {
            toast.success(`Unit of Measure ${isEditing ? 'updated' : 'created'} successfully`);
            onSuccess?.();
        },
        onError: (err) => {
            toast.error(err.response?.data?.error?.message || 'Failed to save UOM');
        }
    });

    const handleChange = (e) => {
        const { name, value } = e.target;
        setForm(prev => ({ ...prev, [name]: value }));
        setErrors(prev => ({ ...prev, [name]: '' }));
    };

    const validate = () => {
        const newErrors = {};
        if (!form.code) newErrors.code = 'Code is required';
        if (!form.name) newErrors.name = 'Name is required';
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        if (!validate()) return;
        mutation.mutate(form);
    };

    return (
        <form onSubmit={handleSubmit} className="uom-form">
            <div className="form-stack">
                <Input
                    name="code"
                    label="Code"
                    placeholder="e.g., PCS, KG, M"
                    value={form.code}
                    onChange={handleChange}
                    error={errors.code}
                    disabled={isEditing}
                    required
                />
                <Input
                    name="name"
                    label="Name"
                    placeholder="e.g., Pieces, Kilogram, Meter"
                    value={form.name}
                    onChange={handleChange}
                    error={errors.name}
                    required
                />
                <TextArea
                    name="description"
                    label="Description"
                    placeholder="Optional description"
                    value={form.description}
                    onChange={handleChange}
                    rows={2}
                />
            </div>

            <div className="form-actions">
                <Button variant="secondary" type="button" onClick={onCancel}>
                    Cancel
                </Button>
                <Button type="submit" loading={mutation.isPending}>
                    {isEditing ? 'Update' : 'Create'} UOM
                </Button>
            </div>
        </form>
    );
}

UOMForm.propTypes = {
    uom: PropTypes.object,
    onSuccess: PropTypes.func,
    onCancel: PropTypes.func
};
